// src/TopPicksRow.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { playClickSound, playTransitionSound } from '../utils/sounds';
import './TopPicksRow.css';
import { FaGraduationCap, FaCode, FaBriefcase, FaHandsHelping, FaProjectDiagram, FaEnvelope, FaMusic, FaBook } from 'react-icons/fa';

// Import images - only need recruiter and developer sets
// Recruiter images
import recruiterSkills from '../images/cover_images/recruiter/skills.jpg';
import recruiterExperience from '../images/cover_images/recruiter/experience.jpg';
import recruiterEducation from '../images/cover_images/recruiter/certifications.jpg';
import recruiterRecommendations from '../images/cover_images/recruiter/recommendations.png';
import recruiterProjects from '../images/cover_images/recruiter/projects.jpg';
import recruiterContactMe from '../images/cover_images/recruiter/contact_me.jpg';

// Developer images
import developerSkills from '../images/cover_images/developer/skills.jpg';
import developerProjects from '../images/cover_images/developer/projects.jpg';
import developerEducation from '../images/cover_images/developer/certifications.jpg';
import developerExperience from '../images/cover_images/developer/experience.jpg';
import developerRecommendations from '../images/cover_images/developer/recommendations.jpg';
import developerContactMe from '../images/cover_images/developer/contact_me.jpg';

type ProfileType = 'recruiter' | 'developer' | 'stalker' | 'adventurer';

interface TopPicksRowProps {
  profile: ProfileType;
}

// Updated order and removed Work Permit
const topPicksConfig = {
  recruiter: [
    { 
      title: "Experience", 
      imgSrc: recruiterExperience,
      icon: <FaBriefcase />, 
      route: "/work-experience" 
    },
    { 
      title: "Skills", 
      imgSrc: recruiterSkills,
      icon: <FaCode />, 
      route: "/skills" 
    },
    { 
      title: "Education", 
      imgSrc: recruiterEducation,
      icon: <FaGraduationCap />, 
      route: "/education" 
    },
    { 
      title: "Projects", 
      imgSrc: recruiterProjects,
      icon: <FaProjectDiagram />, 
      route: "/projects" 
    },
    { 
      title: "Recommendations", 
      imgSrc: recruiterRecommendations,
      icon: <FaHandsHelping />, 
      route: "/recommendations" 
    },
    { 
      title: "Contact Me", 
      imgSrc: recruiterContactMe,
      icon: <FaEnvelope />, 
      route: "/contact-me" 
    }
  ],
  developer: [
    { 
      title: "Skills", 
      imgSrc: developerSkills,
      route: "/skills", 
      icon: <FaCode /> 
    },
    { 
      title: "Projects", 
      imgSrc: developerProjects,
      route: "/projects", 
      icon: <FaProjectDiagram /> 
    },
    { 
      title: "Experience", 
      imgSrc: developerExperience,
      route: "/work-experience", 
      icon: <FaBriefcase /> 
    },
    { 
      title: "Education", 
      imgSrc: developerEducation,
      route: "/education", 
      icon: <FaGraduationCap /> 
    },
    { 
      title: "Recommendations", 
      imgSrc: developerRecommendations,
      route: "/recommendations", 
      icon: <FaHandsHelping /> 
    },
    { 
      title: "Contact Me", 
      imgSrc: developerContactMe,
      route: "/contact-me", 
      icon: <FaEnvelope /> 
    }
  ],
  stalker: [
    { 
      title: "Recommendations", 
      imgSrc: recruiterRecommendations,
      route: "/recommendations", 
      icon: <FaHandsHelping /> 
    },
    { 
      title: "Contact Me", 
      imgSrc: recruiterContactMe,
      route: "/contact-me", 
      icon: <FaEnvelope /> 
    },
    { 
      title: "Projects", 
      imgSrc: recruiterProjects,
      route: "/projects", 
      icon: <FaProjectDiagram /> 
    },
    { 
      title: "Experience", 
      imgSrc: recruiterExperience,
      route: "/work-experience", 
      icon: <FaBriefcase /> 
    },
    { 
      title: "Education", 
      imgSrc: recruiterEducation,
      route: "/education", 
      icon: <FaGraduationCap /> 
    },
  ],
  adventurer: [
    { 
      title: "Music", 
      imgSrc: developerSkills,
      route: "/music", 
      icon: <FaMusic /> 
    },
    { 
      title: "Projects", 
      imgSrc: developerProjects,
      route: "/projects", 
      icon: <FaProjectDiagram /> 
    },
    { 
      title: "Reading", 
      imgSrc: developerEducation,
      route: "/reading", 
      icon: <FaBook /> 
    },
    { 
      title: "Education", 
      imgSrc: developerRecommendations,
      route: "/education", 
      icon: <FaGraduationCap /> 
    },
    { 
      title: "Contact Me", 
      imgSrc: developerContactMe,
      route: "/contact-me", 
      icon: <FaEnvelope /> 
    }
  ]
};

const TopPicksRow: React.FC<TopPicksRowProps> = ({ profile }) => {
  const navigate = useNavigate();
  const topPicks = topPicksConfig[profile];

  const handleCardClick = (route: string) => {
    playClickSound();
    setTimeout(() => {
      playTransitionSound();
      navigate(route, { state: { fromProfile: profile } });
    }, 100);
  };

  return (
    <div className="top-picks-row">
      <h2 className="row-title">Today's Top Picks for {profile}</h2>
      <div className="card-row">
        {topPicks.map((pick, index) => (
          <div 
            key={index} 
            className="pick-card" 
            onClick={() => handleCardClick(pick.route)}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <img src={pick.imgSrc} alt={pick.title} className="pick-image" />
            {/* Hover overlay for desktop */}
            <div className="overlay">
              <div className="pick-label">{pick.title}</div>
            </div>
            {/* Always visible label for mobile */}
            <div className="mobile-label">
              {pick.title}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopPicksRow;